import { computed, toValue } from "vue";
import type { MaybeRefOrGetter } from "vue";

export interface Size {
  width: number;
  height: number;
}

export interface FractionRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface PixelRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export type OverlayFit = "cover" | "contain";

// Fração da margem real (entre o screenRect e a borda da imagem) usada como
// "segura" pra cortar em `cover` — o resto é reservado pro case/bezel do
// celular na foto (que se estende um pouco além do retângulo de tela
// branca, mas não foi medido pixel a pixel). Não é 1.0: sobra uma faixa de
// segurança antes de chegar no bezel de verdade.
const DEFAULT_SAFE_MARGIN_FACTOR = 0.85;

// Escala máxima de `cover` que ainda mantém o corte dentro da margem ao
// redor do celular (nunca corta o próprio aparelho, só a paisagem em
// volta). A margem (fração da imagem, do lado mais apertado — topo/base ou
// esquerda/direita, o que for menor) diz quanto se pode cortar de cada lado
// em espaço natural da imagem; convertido pra um teto de `scale` acima do
// qual o corte ultrapassaria essa margem.
export function computeMaxSafeScale(
  containerSize: Size,
  naturalSize: Size,
  screenRect: FractionRect,
  safeMarginFactor: number = DEFAULT_SAFE_MARGIN_FACTOR,
): number {
  const marginTop = screenRect.top;
  const marginBottom = 1 - screenRect.top - screenRect.height;
  const marginLeft = screenRect.left;
  const marginRight = 1 - screenRect.left - screenRect.width;

  const safeBudgetH = Math.min(marginTop, marginBottom) * naturalSize.height * safeMarginFactor;
  const safeBudgetW = Math.min(marginLeft, marginRight) * naturalSize.width * safeMarginFactor;

  const maxScaleH = containerSize.height / (naturalSize.height - 2 * safeBudgetH);
  const maxScaleW = containerSize.width / (naturalSize.width - 2 * safeBudgetW);

  return Math.min(maxScaleH, maxScaleW);
}

// Matemática de `object-fit: cover`/`object-fit: contain`: dado o tamanho
// real do container e o tamanho natural da imagem, devolve o `scale` e o
// deslocamento (`offsetX`/`offsetY`, relativos ao container) da imagem
// inteira — a peça compartilhada por trás tanto do retângulo de tela quanto
// do próprio `<img>` de fundo.
//
// A única diferença matemática entre os dois modos é o `scale`: `cover` usa
// `Math.max` (a imagem preenche o container inteiro, cortando o que sobra —
// offsets <= 0) e `contain` usa `Math.min` (a imagem cabe inteira dentro do
// container, sobrando letterbox — offsets >= 0). Em `cover`, o `scale` sobe
// direto até `computeMaxSafeScale` — não fica só no mínimo pra cobrir o
// container, usa o zoom máximo que a margem em volta do celular permite.
// Nunca menos que o bruto (`Math.max` no final): garante que a foto ainda
// cobre o container inteiro; se `computeMaxSafeScale` cair abaixo do bruto
// (janela de proporção bem mais extrema que a margem aguenta), o bruto
// vence e o que sobra vira `frameBackgroundColor` (fallback).
function computeCoverLayout(
  containerSize: Size,
  naturalSize: Size,
  screenRect: FractionRect,
  fit: OverlayFit,
) {
  const widthRatio = containerSize.width / naturalSize.width;
  const heightRatio = containerSize.height / naturalSize.height;
  let scale =
    fit === "cover" ? Math.max(widthRatio, heightRatio) : Math.min(widthRatio, heightRatio);

  if (fit === "cover") {
    scale = Math.max(scale, computeMaxSafeScale(containerSize, naturalSize, screenRect));
  }

  const displayedWidth = naturalSize.width * scale;
  const displayedHeight = naturalSize.height * scale;
  const offsetX = (containerSize.width - displayedWidth) / 2;
  const offsetY = (containerSize.height - displayedHeight) / 2;

  return { scale, displayedWidth, displayedHeight, offsetX, offsetY };
}

// Retângulo (em px, relativo ao container) que corresponde a uma fração
// (0-1) da imagem — recalculado sempre que o container muda de tamanho. É o
// que garante que a jornada caia exatamente sobre a área de tela do celular
// do mockup de fundo.
export function computeOverlayRect(
  containerSize: Size,
  naturalSize: Size,
  screenRect: FractionRect,
  fit: OverlayFit = "cover",
): PixelRect {
  if (
    containerSize.width <= 0 ||
    containerSize.height <= 0 ||
    naturalSize.width <= 0 ||
    naturalSize.height <= 0
  ) {
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  const { displayedWidth, displayedHeight, offsetX, offsetY } = computeCoverLayout(
    containerSize,
    naturalSize,
    screenRect,
    fit,
  );

  return {
    left: offsetX + screenRect.left * displayedWidth,
    top: offsetY + screenRect.top * displayedHeight,
    width: screenRect.width * displayedWidth,
    height: screenRect.height * displayedHeight,
  };
}

// Retângulo (em px, relativo ao container) da foto inteira, não só da tela
// do celular — é o que posiciona o `<img>` de fundo. Usa exatamente o mesmo
// `scale`/offsets de `computeOverlayRect`, então os dois nunca divergem.
export function computeOverlayImageRect(
  containerSize: Size,
  naturalSize: Size,
  screenRect: FractionRect,
  fit: OverlayFit = "cover",
): PixelRect {
  if (
    containerSize.width <= 0 ||
    containerSize.height <= 0 ||
    naturalSize.width <= 0 ||
    naturalSize.height <= 0
  ) {
    return { top: 0, left: 0, width: 0, height: 0 };
  }

  const { displayedWidth, displayedHeight, offsetX, offsetY } = computeCoverLayout(
    containerSize,
    naturalSize,
    screenRect,
    fit,
  );

  return { left: offsetX, top: offsetY, width: displayedWidth, height: displayedHeight };
}

export const useOverlayRect = (
  containerSize: MaybeRefOrGetter<Size>,
  naturalSize: MaybeRefOrGetter<Size>,
  screenRect: MaybeRefOrGetter<FractionRect>,
  fit: MaybeRefOrGetter<OverlayFit> = "cover",
) => {
  const rect = computed(() =>
    computeOverlayRect(
      toValue(containerSize),
      toValue(naturalSize),
      toValue(screenRect),
      toValue(fit),
    ),
  );

  const imageRect = computed(() =>
    computeOverlayImageRect(
      toValue(containerSize),
      toValue(naturalSize),
      toValue(screenRect),
      toValue(fit),
    ),
  );

  return { rect, imageRect };
};
